import { useContext, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components'
import moment from 'moment';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faTrashCan,
  faShareNodes,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import WishList from '../../components/Wishlist/WishList';
import userContext from '../../hooks/UserContext';
import Api from '../../utils/Api';
import Loading from '../../components/Loading';
import searchImage from '../../images/searching-list.gif';
import ConfirmButton from '../../components/ConfirmButton';
import Avatar from '../../components/Avatar';
import GenericListImage from '../../images/wishlist.png'
import ListContext from '../../hooks/ListContext';
import EditItem from '../../components/EditItem';

export const fetchList = async (id, context) => {
  if (!id) return null;
  const [error, list] = await Api.getList(id, context);
  if (!error) return list;
  else {
    toast.error(Api.error(error));
    return null;
  }
}

const styles = css`
display: flex;
flex-direction: column;
gap: 10px;
width: 100%;
  & .print-header {
    display: none;
  }
  & .list-header {
    display: flex;
    flex-direction:column;
    flex-wrap: wrap;
    align-items: flex-start;
    width: 100%;
    gap: 10px;
    & .owner-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      width: 100%;
      > .controls {
        display: flex;
        gap: 5px;
        flex-wrap: wrap;
        &.right {
          justify-self: flex-end;
        }
        &.left {
          justify-self: flex-start;
        }
      }
    }

    & .context {
      display: flex;
      flex-wrap: wrap;
      font-size: 1.5em;
      align-self: center;
      text-transform: uppercase;
      color: green;
      text-align:center;
      justify-content: center;
      width: 100%;
    }
    & .list-info {
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: 10px;
      flex-wrap: wrap;
      width: 100%;
      align-items: flex-start;
      padding-bottom: 20px;

      & .list-title {
        display: flex;
        flex-direction: column;
        justify-self: center;
        align-items: center;
        text-align: center;
        width: 100%;
  
      }
      & .list-image {
        min-width: 50px;
        max-width: 150px;
        width: 60%;
        aspect-ratio: 1/1;
      }

      & .list-name {
        font-size: 1.5em;
        font-weight: bold;
      }
      & .owner {
        display: flex;
        flex-direction: column;
        justify-self: center;
        align-items: center;
        text-align: center;
        word-break: break-all;
        width: 100%;
        }
        & .email {
          font-size: 0.75em;
        }
      }
    }

    & .collaborator {
      background-color: green;
      color: white;
      font-weight: bold;
      text-transform: uppercase;
      font-size: .8em;
      padding: 8px;
      border-radius: 3px;
      text-align: center;
      justify-self: center;
      align-self: center;
    }
  
  & .list-tabs {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  @media print {
    & .print-header {
      display: flex;
      width: 100%;
      flex-direction: column;
      align-items: center;
      line-height: 2em;
      & .name {
        text-transform: capitalize;
      }
    }
    & .list-header {
      display: none;
    }
  }

`
const ListDetails = styled(({ className }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useContext(userContext);
  const navigate = useNavigate();
  const [isImporting, setIsImporting] = useState(false);

  const model = searchParams.get('context');
  const contextId = searchParams.get('id');
  const context = { model, id: contextId };

  const { isLoading, data: list, refetch } = useQuery(['list-details', id, context], () => fetchList(id, context));

  const isOwner = list?.user?._id === user?._id;
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const group = list?.sharedWith?.groups?.find(group => group._id.toString() === context?.id) ?? {};

  const isCollaborating = useMemo(() => {
    const isCollaboratingUser = list?.collaborateWith?.users?.find(cUser => cUser._id === user._id) ? true : false;
    const isCollaboratingGroup = list?.collaborateWith?.groups?.find(group => group.members.find(gUserId => gUserId === user._id)) ? true : false;
    return (isCollaboratingGroup || isCollaboratingUser) && !isOwner;
  }, [list, user, isOwner]);

  const canDeleteItem = isOwner;
  const canAddItem = isOwner || isCollaborating;
  const canEditItem = isOwner || isCollaborating;

  const deleteList = async () => {
    const [error,] = await Api.deleteList(id);
    if (!error) {
      toast.success('List deleted');
      navigate(-1);
    } else {
      toast.error(Api.error(error));
    }
  };

  const shareList = async () => {
    navigate(`/lists/share/${list._id}`);
  }

  const deleteItem = async item => {
    const [error,] = await Api.deleteListItem({ listId: id, itemId: item._id });
    if (!error) {
      toast.success('Item deleted');
      refetch();
    } else {
      toast.error(Api.error(error));
    }
  };

  const addItem = async item => {
    setShowEditItemModal(false);
    setSelectedItem(null);
    const [error,] = await Api.addItemToList(list._id, item);
    if (error) {
      toast.error(Api.error(error));
      console.error(error);
    } else {
      toast.success('Item added successfully');
      refetch();
    }
  }

  const updateItem = async item => {
    setShowEditItemModal(false);
    setSelectedItem(null);
    const { _id, ...updates } = item ?? {};
    const [error,] = await Api.updateItem(list._id, item._id, updates);
    if (error) {
      toast.error(Api.error(error));
      console.error(error);
    } else {
      toast.success('Item updated successfully');
      refetch();
    }
  }

  const handleCancelEdit = () => {
    setSelectedItem(null);
    setShowEditItemModal(false);
  }

  const handleOnEditItem = item => {
    if (item) setSelectedItem(item);
    setShowEditItemModal(true);
  }

  const claimItem = async item => {
    const [error,] = await Api.claimItem(item);
    if (error) {
      console.error(error);
      return toast.error(Api.error(error));
    } else {
      toast.success('Item has been reserved');
      refetch();
    }
  }

  const releaseItem = async item => {
    const [error,] = await Api.releaseItem(item);
    if (error) {
      console.error(error);
      return toast.error(Api.error(error));
    } else {
      toast.success('Item has been released');
      refetch();
    }
  }

  const refreshList = async () => {
    setIsImporting(true);
    const [error, ] = await Api.importList(list._id);
    setIsImporting(false);
    if (error) {
      console.log(error);
      return toast.error(Api.error(error));
    } else {
      toast.success('List has been refreshed');
      refetch();
    }
  }

  const listContext = {
    refetchList: refetch,
    list,
    context,
    isOwner,
    members: group?.members ?? [],
    isCollaborating,
    canAddItem,
    canEditItem,
    canDeleteItem,
    onAddItem: canAddItem ? () => handleOnEditItem() : null,
    onEditItem: canEditItem ? handleOnEditItem : null,
    onDeleteItem: canDeleteItem ? deleteItem : null,
    onClaimItem: claimItem,
    onReleaseItem: releaseItem,
  }

  return (
    <Loading isLoading={isLoading} loadingImage={searchImage} loadingText='Importing list from Amazon...'>
      <div className={className}>
        {list &&
          <>
            <div className = 'print-header'>
              <div className = 'name'>{list.name}</div>
              <div className='name'>{`${list.user.firstname} ${list.user.lastname}`}</div>
            </div>
            <div className='list-header'>
              {
                isOwner &&
                <div className='owner-row'>
                  <div className='controls left'>
                    <button className='primary' onClick={() => shareList()}>
                      <FontAwesomeIcon icon={faShareNodes} />
                    </button>
                    <button disabled = {isImporting} className='primary' onClick={() => refreshList()}>
                     <FontAwesomeIcon icon={faRefresh} spin = {isImporting}/>
                    </button>
                  </div>
                  <div className='controls right'>
                    <button className='primary' onClick={() => navigate(`/lists/edit/${list._id}`)}>Edit</button>
                    <ConfirmButton
                      text={`Do you want to delete the list "${list?.name}"?`}
                      confirm='Yes'
                      cancel='No'
                      className='danger'
                      onConfirm={() => deleteList()}
                      onCancel={() => { }}>
                      <FontAwesomeIcon icon={faTrashCan} />
                    </ConfirmButton>
                  </div>
                </div>
              }
              <div className='context'>{group?.name ? `${group.name} group` : ''}</div>
              <div className='list-info'>
                <div className='list-title'>
                  <div className='list-image'>
                    <Avatar src={list.image} defaultImage={GenericListImage} />
                  </div>
                  <div className='list-name'>
                    {list.name}
                  </div>
                  {
                    list.eventDate &&
                    <div className='list-date'>
                      {moment(list.eventDate).format('MM/DD/YYYY')}
                    </div>
                  }
                </div>
                <div className='owner'>
                  <div className='list-image'>
                    <Avatar src={list.user.avatar} />
                  </div>
                  <div className='name'>{`${list.user.firstname} ${list.user.lastname}`}</div>
                  <div className='email'>{list.user.email}</div>
                </div>
              </div>
              {
                isCollaborating &&
                <div className='collaborator'>
                  Collaborator
                </div>
              }
              {
                isOwner &&
                <div className='collaborator'>
                  Owner
                </div>
              }
            </div>
          </>
        }
        <ListContext.Provider value={listContext}>
          <WishList />
        </ListContext.Provider>
        {
          showEditItemModal && <EditItem
            item={selectedItem}
            title={selectedItem ? 'Edit Item' : "Add New Item"}
            onSave={selectedItem ? updateItem : addItem}
            onCancel={handleCancelEdit}
          />
        }
      </div>
    </Loading>
  )
})`${styles}`;

export default ListDetails;
